import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// Axios is configured with interceptors in index.js

const WIP = () => {
  const navigate = useNavigate();
  const [wipData, setWipData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [projects, setProjects] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [csvExportLoading, setCsvExportLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [eacEnabled, setEacEnabled] = useState(false);
  const [wipSettings, setWipSettings] = useState(null);
  const [showPendingCOModal, setShowPendingCOModal] = useState(false);
  const [selectedProjectForPCO, setSelectedProjectForPCO] = useState(null);
  const [selectedPeriodForPCO, setSelectedPeriodForPCO] = useState(null);
  const [pendingChangeOrders, setPendingChangeOrders] = useState([]);
  const [pcoLoading, setPcoLoading] = useState(false);
  const [postingLoading, setPostingLoading] = useState({});
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [calculationData, setCalculationData] = useState(null);

  useEffect(() => {
    console.log('WIP component mounted, calling fetchInitialData...');
    fetchInitialData();
    fetchWipSettings();
  }, []);

  // Refetch WIP data when selected period changes
  useEffect(() => {
    if (selectedPeriod) {
      console.log('Selected period changed, refetching WIP data...');
      fetchWipData(selectedPeriod);
    }
  }, [selectedPeriod]);

  const fetchWipSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/wip-settings`);
      setWipSettings(response.data);
      
      // Check if EAC reporting is enabled
      const eacSetting = response.data.find(setting => setting.setting_name === 'use_eac_reporting');
      setEacEnabled(eacSetting && eacSetting.setting_value === 'true');
      
      console.log('WIP Settings loaded:', response.data);
      console.log('EAC Enabled:', eacSetting && eacSetting.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching WIP settings:', err);
    }
  };

  const fetchPendingChangeOrders = async (projectVuid, periodVuid) => {
    try {
      setPcoLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/pending-change-orders`, {
        params: {
          project_vuid: projectVuid,
          accounting_period_vuid: periodVuid
        }
      });
      // Filter to only show PCOs included in forecast
      const pcos = response.data.data || response.data || [];
      const includedPCOs = pcos.filter(pco => pco.is_included_in_forecast);
      setPendingChangeOrders(includedPCOs);
    } catch (err) {
      console.error('Error fetching pending change orders:', err);
      alert('Failed to load pending change orders: ' + (err.response?.data?.error || err.message));
    } finally {
      setPcoLoading(false);
    }
  };

  const handlePendingCOClick = (e, projectVuid, periodVuid) => {
    e.stopPropagation(); // Prevent row click
    setSelectedProjectForPCO(projectVuid);
    setSelectedPeriodForPCO(periodVuid);
    setShowPendingCOModal(true);
    fetchPendingChangeOrders(projectVuid, periodVuid);
  };

  const handleRemovePendingCO = async (pcoVuid) => {
    if (!window.confirm('Are you sure you want to remove this pending change order from the forecast?')) {
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/pending-change-orders/${pcoVuid}`, {
        is_included_in_forecast: false
      });
      
      // Refresh pending change orders list
      fetchPendingChangeOrders(selectedProjectForPCO, selectedPeriodForPCO);
      
      // Refresh WIP data to update totals
      fetchWipData(selectedPeriod);
      
      alert('Pending change order removed from forecast successfully');
    } catch (err) {
      console.error('Error removing pending change order:', err);
      alert('Failed to remove pending change order from forecast: ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePostAllOverUnderBilling = async () => {
    if (!selectedPeriod) {
      alert('Please select an accounting period first');
      return;
    }

    // Find all projects with over/under billing
    const projectsWithOverUnderBilling = (wipData || []).filter(item => 
      (item.over_billing || 0) > 0 || (item.under_billing || 0) > 0
    );

    if (projectsWithOverUnderBilling.length === 0) {
      alert('No projects have over/under billing amounts to post');
      return;
    }

    const totalOverBilling = projectsWithOverUnderBilling.reduce((sum, item) => sum + (item.over_billing || 0), 0);
    const totalUnderBilling = projectsWithOverUnderBilling.reduce((sum, item) => sum + (item.under_billing || 0), 0);
    
    if (!window.confirm(`Are you sure you want to post over/under billing adjustments for ${projectsWithOverUnderBilling.length} projects?\n\nTotal Overbilled: ${formatCurrency(totalOverBilling)}\nTotal Underbilled: ${formatCurrency(totalUnderBilling)}`)) {
      return;
    }

    setPostingLoading(prev => ({ ...prev, 'all': true }));

    try {
      let successCount = 0;
      let errorCount = 0;

      // Post each project's over/under billing
      for (const project of projectsWithOverUnderBilling) {
        try {
          // Post over billing if exists
          if ((project.over_billing || 0) > 0) {
            await axios.post(`${API_BASE_URL}/api/posted-records/post`, {
              transaction_type: 'over_under_billing',
              project_vuid: project.project_vuid,
              accounting_period_vuid: selectedPeriod,
              over_under_amount: project.over_billing,
              posted_by: 'User'
            });
            successCount++;
          }

          // Post under billing if exists
          if ((project.under_billing || 0) > 0) {
            await axios.post(`${API_BASE_URL}/api/posted-records/post`, {
              transaction_type: 'over_under_billing',
              project_vuid: project.project_vuid,
              accounting_period_vuid: selectedPeriod,
              over_under_amount: -project.under_billing,
              posted_by: 'User'
            });
            successCount++;
          }
        } catch (err) {
          console.error(`Error posting for project ${project.project_number}:`, err);
          errorCount++;
        }
      }
      
      // Refresh WIP data to show updated posted status
      fetchWipData(selectedPeriod);
      
      if (errorCount === 0) {
        alert(`Successfully posted ${successCount} over/under billing adjustments`);
      } else {
        alert(`Posted ${successCount} adjustments successfully, ${errorCount} failed`);
      }
    } catch (err) {
      console.error('Error posting over/under billing:', err);
      alert('Failed to post over/under billing adjustments: ' + (err.response?.data?.error || err.message));
    } finally {
      setPostingLoading(prev => ({ ...prev, 'all': false }));
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching projects...');
      // Fetch projects for filtering
      const projectsResponse = await axios.get(`${API_BASE_URL}/api/projects`);
      console.log('Projects response:', projectsResponse.data);
      setProjects(projectsResponse.data);

      console.log('Fetching accounting periods...');
      // Fetch accounting periods for filtering
      const periodsResponse = await axios.get(`${API_BASE_URL}/api/accounting-periods`);
      console.log('Accounting periods response:', periodsResponse.data);
      
      // Sort periods: open periods first, then closed periods by year/month descending
      const sortedPeriods = periodsResponse.data.sort((a, b) => {
        if (a.status === 'open' && b.status !== 'open') return -1;
        if (a.status !== 'open' && b.status === 'open') return + 1;
        if (a.status === 'open' && b.status === 'open') {
          // Sort open periods by year/month descending
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        }
        // Sort closed periods by year/month descending
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
      
      setAccountingPeriods(sortedPeriods);
      
      // Set default selected period to the first open period only if no period is currently selected
      if (!selectedPeriod) {
        const openPeriod = sortedPeriods.find(p => p.status === 'open');
        if (openPeriod) {
          setSelectedPeriod(openPeriod.vuid);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(`Failed to load initial data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchWipData = async (periodVuid) => {
    try {
      setLoading(true);
      
      console.log('Fetching WIP data...');
      // Fetch actual WIP data with period filter if selected
      let wipUrl = `${API_BASE_URL}/api/wip`;
      if (periodVuid) {
        wipUrl += `?accounting_period_vuid=${periodVuid}`;
        console.log('Fetching WIP data with period filter:', periodVuid);
      } else {
        console.log('Fetching WIP data without period filter');
      }
      console.log('WIP URL:', wipUrl);
      const wipResponse = await axios.get(wipUrl);
      console.log('WIP response:', wipResponse.data);
      setWipData(wipResponse.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching WIP data:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(`Failed to load WIP data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key) return wipData;

    return [...wipData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return + 0;
      }

      return + 0;
    });
  };

  const getFilteredData = () => {
    try {
      let filtered = getSortedData();

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(item =>
          item.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.contract_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply project filter
      if (projectFilter !== 'all') {
        filtered = filtered.filter(item => item.project_number === projectFilter);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        filtered = filtered.filter(item => item.status === statusFilter);
      }

      // Apply accounting period filter
      if (selectedPeriod) {
        const selectedPeriodData = accountingPeriods.find(p => p.vuid === selectedPeriod);
        if (selectedPeriodData) {
          filtered = filtered.filter(item => {
            // Show data from selected period and prior periods
            // This assumes the WIP data includes accounting period information
            // If not, we'll need to modify the backend to include this
            return true; // For now, show all data until we implement period filtering
          });
        }
      }

      return filtered;
    } catch (error) {
      console.error('Error in getFilteredData:', error);
      return wipData || [];
    }
  };

  const getCurrentData = () => {
    try {
      const filtered = getFilteredData();
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filtered.slice(startIndex, endIndex);
    } catch (error) {
      console.error('Error in getCurrentData:', error);
      return [];
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // CSV Export functions
  const downloadCsvFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const generateJournalEntriesCsv = (previewData) => {
    const headers = [
      'Type', 'Reference Number', 'Project Name', 'Project Number', 
      'Account', 'Description', 'Debit Amount', 'Credit Amount'
    ];
    
    const rows = [];
    previewData.preview_entries.forEach(entry => {
      entry.line_items.forEach(lineItem => {
        rows.push([
          entry.type,
          entry.reference_number,
          entry.project_name,
          entry.project_number,
          lineItem.account,
          lineItem.description || '',
          lineItem.debit_amount || 0,
          lineItem.credit_amount || 0
        ]);
      });
    });
    
    return [headers, ...rows].map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  };

  const generateSummaryCsv = (previewData) => {
    const headers = [
      'Metric', 'Value'
    ];
    
    const rows = [
      ['Total Entries', previewData.preview_summary.total_entries],
      ['AP Invoices', previewData.preview_summary.ap_invoices],
      ['Project Billings', previewData.preview_summary.project_billings],
      ['Labor Costs', previewData.preview_summary.labor_costs || 0],
      ['Project Expenses', previewData.preview_summary.project_expenses || 0],
      ['Retainage Entries', previewData.preview_summary.retainage_entries || 0],
    ];
    
    return [headers, ...rows].map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  };

  const handleCsvExport = () => {
    if (!previewData) return;
    
    try {
      setCsvExportLoading(true);
      const csvContent = generateJournalEntriesCsv(previewData);
      const filename = `journal_entries_preview_${previewData.accounting_period.month}_${previewData.accounting_period.year}.csv`;
      downloadCsvFile(csvContent, filename);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setCsvExportLoading(false);
    }
  };

  const handleSummaryCsvExport = () => {
    if (!previewData) return;
    
    try {
      setCsvExportLoading(true);
      const csvContent = generateSummaryCsv(previewData);
      const filename = `journal_entries_summary_${previewData.accounting_period.month}_${previewData.accounting_period.year}.csv`;
      downloadCsvFile(csvContent, filename);
    } catch (error) {
      console.error('Error exporting summary CSV:', error);
    } finally {
      setCsvExportLoading(false);
    }
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '0.000%';
    return `${value.toFixed(3)}%`;
  };

  const handlePreviewJournalEntries = async () => {
    if (!selectedPeriod) {
      alert('Please select an accounting period first');
      return;
    }

    try {
      setPreviewLoading(true);
      
      console.log('Previewing journal entries for period:', selectedPeriod);
      
      // Call the preview API endpoint
      const response = await axios.get(`${API_BASE_URL}/api/journal-entries/preview-from-posted/${selectedPeriod}`);
      
      console.log('Preview response:', response.data);
      
      if (response.data.success) {
        // Log all entries to find ones with missing data
        if (response.data.journal_entries && response.data.journal_entries.length > 0) {
          console.log(`Total entries: ${response.data.journal_entries.length}`);
          response.data.journal_entries.forEach((entry, idx) => {
            const missingData = !entry.type && !entry.reference_type && !entry.reference_number && !entry.project_name;
            if (missingData || idx < 3) {
              console.log(`Entry ${idx}:`, {
                type: entry.type,
                reference_type: entry.reference_type,
                reference_number: entry.reference_number,
                project_name: entry.project_name,
                project_number: entry.project_number,
                line_items_count: entry.line_items?.length || 0,
                hasMissingData: missingData
              });
            }
          });
        }
        
        // Transform the response to match expected structure
        const transformedData = {
          ...response.data,
          preview_entries: response.data.journal_entries,
          accounting_period: {
            month: response.data.accounting_period?.month || new Date().getMonth() + 1,
            year: response.data.accounting_period?.year || new Date().getFullYear()
          }
        };
        setPreviewData(transformedData);
        setShowPreviewModal(true);
      } else {
        alert(`Error previewing journal entries: ${response.data.error}`);
      }
    } catch (err) {
      console.error('Error previewing journal entries:', err);
      const errorMessage = err.response?.data?.error || err.message || err.toString() || 'Unknown error occurred';
      alert(`Error previewing journal entries: ${errorMessage}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCloseMonth = async () => {
    if (!selectedPeriod) {
      alert('Please select an accounting period first');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Closing month for period:', selectedPeriod);
      
      // Call the close month API endpoint
      const response = await axios.post(`${API_BASE_URL}/api/wip/close-month`, {
        accounting_period_vuid: selectedPeriod
      });
      
      console.log('Close month response:', response.data);
      
      if (response.data.success) {
        alert(`Month closed successfully! Created ${response.data.journal_entries_created} journal entries.`);
        // Refresh the data to show updated status
        fetchWipData(selectedPeriod);
        
        // Navigate to Journal Entries page filtered to the closed period
        const selectedPeriodData = accountingPeriods.find(p => p.vuid === selectedPeriod);
        if (selectedPeriodData) {
          navigate(`/journal-entries?accounting_period_vuid=${selectedPeriod}`);
        }
      } else {
        alert(`Error closing month: ${response.data.error}`);
      }
    } catch (err) {
      console.error('Error closing month:', err);
      alert(`Failed to close month: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  const handleProjectClick = (projectVuid) => {
    navigate(`/projects/${projectVuid}`);
  };

  const handleShowCalculationDetails = async (e, item) => {
    e.stopPropagation(); // Prevent row click navigation
    
    // Calculate the values for display
    const budgetValue = eacEnabled && item.estimated_at_completion ? item.estimated_at_completion : (item.current_budget_amount || 0);
    const costsToDate = item.costs_to_date || 0;
    const totalContractAmount = item.total_contract_amount || 0;
    const usesContractItemFeeLevel = item.uses_contract_item_fee_level || false;
    
    // Calculate percent complete: (Costs to Date / Budget) * 100 (standard method)
    // Note: For contract_item fee level, percent complete is calculated per cost code and weighted
    const percentCompleteCalc = budgetValue > 0 ? (costsToDate / budgetValue) * 100 : 0;
    
    // Calculate earned revenue: (Costs to Date / Budget) * Total Contract Amount (standard method)
    // Note: For contract_item fee level, revenue is calculated per cost code/type combination
    const earnedRevenueCalc = budgetValue > 0 ? (costsToDate / budgetValue) * totalContractAmount : 0;
    
    // Fetch detailed breakdown from API
    let detailedBreakdown = null;
    try {
      const params = selectedPeriod ? { accounting_period_vuid: selectedPeriod } : {};
      const response = await axios.get(`${API_BASE_URL}/api/wip/${item.project_vuid}/calculation-details`, { params });
      detailedBreakdown = response.data;
    } catch (error) {
      console.error('Error fetching calculation details:', error);
    }
    
    setCalculationData({
      project_number: item.project_number,
      project_name: item.project_name,
      project_vuid: item.project_vuid,
      costs_to_date: costsToDate,
      budget_value: budgetValue,
      budget_label: eacEnabled && item.estimated_at_completion ? 'Estimated Cost at Completion (EAC)' : 'Current Budget',
      total_contract_amount: totalContractAmount,
      percent_complete: item.percent_complete || 0,
      percent_complete_calc: percentCompleteCalc,
      revenue_recognized: item.revenue_recognized || 0,
      earned_revenue_calc: earnedRevenueCalc,
      eac_enabled: eacEnabled,
      uses_contract_item_fee_level: usesContractItemFeeLevel,
      detailed_breakdown: detailedBreakdown
    });
    setShowCalculationModal(true);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vermillion-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Project Contracts WIP Report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Project Contracts WIP Report</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              fetchInitialData();
              if (selectedPeriod) {
                fetchWipData(selectedPeriod);
              }
            }}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const filteredData = getFilteredData();
  const currentData = getCurrentData();
  const totalPages = Math.ceil((filteredData?.length || 0) / itemsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3 font-sans">
            Project Contracts WIP Report
            {eacEnabled && (
              <span className="ml-3 text-2xl text-indigo-600 font-semibold">
                (EAC Version)
              </span>
            )}
          </h1>
          <p className="text-lg text-gray-700 font-light">
            Track project progress, billings, and earned value across all project contracts
            {selectedPeriod && (
              <span className="block mt-2 text-vermillion-600 font-medium">
                Data as of: {(() => {
                  const period = accountingPeriods.find(p => p.vuid === selectedPeriod);
                  return period ? `${period.month}/${period.year} ${period.status === 'open' ? '(Open Period)' : '(Closed Period)'}` : '';
                })()}
              </span>
            )}
            {eacEnabled && (
              <span className="block mt-2 text-sm text-indigo-600 bg-indigo-50 inline-block px-4 py-2 rounded-lg">
                Using Estimate at Completion (EAC) methodology - Percent complete calculated using EAC values from buyout & forecasting
              </span>
            )}
          </p>
        </div>

        {/* Close Month Buttons */}
        {selectedPeriod && (
          <div className="mb-6 flex justify-center gap-4">
            <button
              onClick={handlePreviewJournalEntries}
              disabled={previewLoading}
              className={`flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors ${
                previewLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-tooltip="Preview Journal Entries"
            >
              {previewLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-gray-900" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  <span>Previewing...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Preview Journal Entries</span>
                </>
              )}
            </button>
            <button
              onClick={handlePostAllOverUnderBilling}
              disabled={postingLoading['all']}
              className={`flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors ${
                postingLoading['all'] ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-tooltip="Post All Over/Under Billing"
            >
              {postingLoading['all'] ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  <span>Posting...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>Post All Over/Under Billing</span>
                </>
              )}
            </button>
            <button
              onClick={handleCloseMonth}
              className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
              data-tooltip="Close Month - Generate Journal Entries"
            >
              <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Close Month - Generate Journal Entries</span>
            </button>
          </div>
        )}

        {/* Summary Stats */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.total_contract_amount || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Contract Value</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => {
                const budgetValue = eacEnabled && item.estimated_at_completion ? item.estimated_at_completion : (item.current_budget_amount || 0);
                return sum + budgetValue;
              }, 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">{eacEnabled ? 'Total Est. Cost at Completion' : 'Total Current Budget'}</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.costs_to_date || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Costs to Date</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.total_project_change_orders || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Project Change Orders</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.project_billings || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Project Billings (Including Retainage)</div>
          </div>
          

          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {(filteredData || []).length}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Active Contracts</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => {
                const budgetValue = eacEnabled && item.estimated_at_completion ? item.estimated_at_completion : (item.current_budget_amount || 0);
                return sum + ((item.total_contract_amount || 0) - budgetValue);
              }, 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Profit</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.revenue_recognized || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Earned Revenue</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.over_billing || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Overbilled</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {formatCurrency((filteredData || []).reduce((sum, item) => sum + (item.under_billing || 0), 0))}
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Total Underbilled</div>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center">
            <div className="text-lg font-bold text-blue-600 mb-1">
              {(filteredData || []).length > 0 
                ? `${((filteredData || []).reduce((sum, item) => sum + (item.percent_complete || 0), 0) / (filteredData || []).length).toFixed(3)}%`
                : '0.000%'
              }
            </div>
            <div className="text-blue-800 font-medium text-xs leading-tight">Avg % Complete</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-2xl p-6 mb-8 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search projects, contracts, customers..."
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent"
              />
            </div>

            {/* Project Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Project
              </label>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent"
              >
                <option value="all">All Projects</option>
                {projects.map((project) => (
                  <option key={project.vuid} value={project.project_number}>
                    {project.project_number} - {project.project_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
              </select>
            </div>

            {/* Accounting Period Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                As Of Period
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => {
                  console.log('Period selection changed to:', e.target.value);
                  setSelectedPeriod(e.target.value);
                }}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-transparent"
              >
                <option value="">Select Period</option>
                {accountingPeriods.map((period) => (
                  <option key={period.vuid} value={period.vuid}>
                    {period.month}/{period.year} {period.status === 'open' ? '(Open)' : '(Closed)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setProjectFilter('all');
                  setStatusFilter('all');
                  const openPeriod = accountingPeriods.find(p => p.status === 'open');
                  if (openPeriod) {
                    setSelectedPeriod(openPeriod.vuid);
                  }
                }}
                className="w-full px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* WIP Report Grid */}
        {currentData && currentData.length > 0 ? (
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('project_number')}>
                    <div className="flex items-center space-x-1">
                      <span>Project</span>
                      {getSortIcon('project_number')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('contract_number')}>
                    <div className="flex items-center space-x-1">
                      <span>Contract</span>
                      {getSortIcon('contract_number')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('original_contract_amount')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Original Amount</span>
                      {getSortIcon('original_contract_amount')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('change_orders')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Change Orders</span>
                      {getSortIcon('change_orders')}
                    </div>
                  </th>
                  {eacEnabled && (
                    <th className="px-4 py-3 text-right text-xs font-semibold text-indigo-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-indigo-50" onClick={() => handleSort('pending_change_orders_revenue')}>
                      <div className="flex items-center justify-end space-x-1">
                        <span>Pending CO Revenue</span>
                        {getSortIcon('pending_change_orders_revenue')}
                      </div>
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('total_contract_amount')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Total Contract</span>
                      {getSortIcon('total_contract_amount')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('original_budget_amount')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Original Budget</span>
                      {getSortIcon('original_budget_amount')}
                    </div>
                  </th>
                  {eacEnabled && (
                    <th className="px-4 py-3 text-right text-xs font-semibold text-indigo-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-indigo-50" onClick={() => handleSort('pending_change_orders_budget')}>
                      <div className="flex items-center justify-end space-x-1">
                        <span>Pending CO Budget</span>
                        {getSortIcon('pending_change_orders_budget')}
                      </div>
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('current_budget_amount')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>{eacEnabled ? 'Est. Cost at Completion' : 'Current Budget'}</span>
                      {getSortIcon('current_budget_amount')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('costs_to_date')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Costs to Date</span>
                      {getSortIcon('costs_to_date')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('project_billings')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Billed to Date</span>
                      {getSortIcon('project_billings')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('percent_complete')}>
                    <div className="flex items-center justify-center space-x-1">
                      <span>% Complete</span>
                      {getSortIcon('percent_complete')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('earned_revenue')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Earned Revenue</span>
                      {getSortIcon('earned_revenue')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('profit_margin_percent')}>
                    <div className="flex items-center justify-center space-x-1">
                      <span>Profit Margin %</span>
                      {getSortIcon('profit_margin_percent')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('profit')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Profit</span>
                      {getSortIcon('profit')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('over_billing')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Overbilled</span>
                      {getSortIcon('over_billing')}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('under_billing')}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>Underbilled</span>
                      {getSortIcon('under_billing')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(currentData || []).map((item) => (
                  <tr 
                    key={item.id || item.vuid || Math.random()} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleProjectClick(item.project_vuid)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 hover:text-vermillion-600 transition-colors">{item.project_number}</div>
                        <div className="text-sm text-gray-500">{item.project_name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{item.contract_number}</div>
                        <div className="text-sm text-gray-500">{item.contract_name}</div>
                        <div className="text-sm text-gray-400">{item.customer_name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.original_contract_amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.change_orders || 0)}
                    </td>
                    {eacEnabled && (
                      <td 
                        className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-indigo-700 bg-indigo-50 cursor-pointer hover:bg-indigo-100 transition-colors"
                        onClick={(e) => handlePendingCOClick(e, item.project_vuid, selectedPeriod)}
                        data-tooltip="Click to view pending change orders"
                      >
                        <div className="flex items-center justify-end space-x-1">
                          <span>{formatCurrency(item.pending_change_orders_revenue || 0)}</span>
                          {(item.pending_change_orders_revenue || 0) > 0 && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {formatCurrency(item.total_contract_amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.original_budget_amount)}
                    </td>
                    {eacEnabled && (
                      <td 
                        className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-indigo-700 bg-indigo-50 cursor-pointer hover:bg-indigo-100 transition-colors"
                        onClick={(e) => handlePendingCOClick(e, item.project_vuid, selectedPeriod)}
                        data-tooltip="Click to view pending change orders"
                      >
                        <div className="flex items-center justify-end space-x-1">
                          <span>{formatCurrency(item.pending_change_orders_budget || 0)}</span>
                          {(item.pending_change_orders_budget || 0) > 0 && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {formatCurrency(eacEnabled && item.estimated_at_completion ? item.estimated_at_completion : (item.current_budget_amount || 0))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.costs_to_date || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.project_billings || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <div className="inline-flex items-center gap-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.percent_complete >= 100 ? 'bg-green-100 text-green-800' :
                          item.percent_complete >= 75 ? 'bg-blue-100 text-blue-800' :
                          item.percent_complete >= 50 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {formatPercent(item.percent_complete)}
                        </span>
                        <button
                          onClick={(e) => handleShowCalculationDetails(e, item)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          data-tooltip="Show calculation details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      <div className="flex items-center justify-end gap-1">
                        <span>{formatCurrency(item.revenue_recognized || 0)}</span>
                        <button
                          onClick={(e) => handleShowCalculationDetails(e, item)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          data-tooltip="Show calculation details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (item.profit_margin_percent || 0) > 10 ? 'bg-green-100 text-green-800' :
                        (item.profit_margin_percent || 0) > 5 ? 'bg-yellow-100 text-yellow-800' :
                        (item.profit_margin_percent || 0) > 0 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {formatPercent(item.profit_margin_percent || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      <span className={`font-semibold ${
                        (() => {
                          const budgetValue = eacEnabled && item.estimated_at_completion ? item.estimated_at_completion : (item.current_budget_amount || 0);
                          const profit = (item.total_contract_amount || 0) - budgetValue;
                          return profit > 0 ? 'text-green-600' : profit < 0 ? 'text-red-600' : 'text-gray-900';
                        })()
                      }`}>
                        {(() => {
                          const budgetValue = eacEnabled && item.estimated_at_completion ? item.estimated_at_completion : (item.current_budget_amount || 0);
                          return formatCurrency((item.total_contract_amount || 0) - budgetValue);
                        })()}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.over_billing || 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(item.under_billing || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, (filteredData || []).length)} of {(filteredData || []).length} results
            </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => page === 1 || page === totalPages || 
                                     (page >= currentPage - 2 && page <= currentPage + 2))
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-3 py-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => paginate(page)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium ${
                            currentPage === page
                              ? 'bg-black text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                  
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        ) : (
          <div className="bg-white rounded-xl shadow-2xl p-8 border border-gray-200 text-center">
            <div className="text-gray-500 text-lg mb-4">No WIP data available</div>
            <button
              onClick={() => {
                if (selectedPeriod) {
                  fetchWipData(selectedPeriod);
                }
              }}
              className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Refresh Data
            </button>
          </div>
        )}

        {/* Journal Entries Preview Modal */}
        {showPreviewModal && previewData && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Journal Entries Preview - {previewData.accounting_period.month}/{previewData.accounting_period.year}
                  </h3>
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Summary */}
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Preview Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">Total Entries:</span>
                      <span className="ml-2 text-blue-900">{previewData.preview_summary.total_entries}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">AP Invoices:</span>
                      <span className="ml-2 text-blue-900">{previewData.preview_summary.ap_invoices}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Project Billings:</span>
                      <span className="ml-2 text-blue-900">{previewData.preview_summary.project_billings}</span>
                    </div>
                                <div>
              <span className="text-blue-700 font-medium">Labor Costs:</span>
              <span className="ml-2 text-blue-900">{previewData.preview_summary.labor_costs || 0}</span>
            </div>
            <div>
              <span className="text-purple-700 font-medium">Project Expenses:</span>
              <span className="ml-2 text-purple-900">{previewData.preview_summary.project_expenses || 0}</span>
            </div>
            <div>
              <span className="text-orange-700 font-medium">Retainage Entries:</span>
              <span className="ml-2 text-orange-900">{previewData.preview_summary.retainage_entries || 0}</span>
            </div>
            <div>
              <span className="text-green-700 font-medium">Total Debits:</span>
              <span className="ml-2 text-green-900">{formatCurrency(previewData.preview_summary.total_debits || 0)}</span>
            </div>
            <div>
              <span className="text-red-700 font-medium">Total Credits:</span>
              <span className="ml-2 text-red-900">{formatCurrency(previewData.preview_summary.total_credits || 0)}</span>
            </div>
            {previewData.preview_summary.validation_errors && previewData.preview_summary.validation_errors.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-red-600 text-lg mr-2">⚠️</span>
                  <span className="text-red-800 font-semibold">Accounting Validation Errors</span>
                </div>
                <div className="text-red-700 text-sm">
                  {previewData.preview_summary.validation_errors.map((error, index) => (
                    <div key={index} className="mb-1">{error}</div>
                  ))}
                </div>
              </div>
            )}
            {previewData.preview_summary.is_balanced && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <span className="text-green-600 text-lg mr-2">✅</span>
                  <span className="text-green-800 font-semibold">All journal entries are properly balanced</span>
                </div>
              </div>
            )}
                  </div>
                </div>

                {/* Preview Entries */}
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.preview_entries.map((entry, entryIndex) => {
                        const lineItems = entry.line_items || [];
                        const rowSpan = Math.max(lineItems.length, 1);
                        
                        // Debug entries with missing data
                        const hasMissingData = !entry.type && !entry.reference_type && !entry.reference_number && !entry.project_name;
                        if (hasMissingData || entryIndex < 3) {
                          console.log(`Entry ${entryIndex}:`, {
                            type: entry.type,
                            reference_type: entry.reference_type,
                            reference_number: entry.reference_number,
                            project_name: entry.project_name,
                            project_number: entry.project_number,
                            hasMissingData,
                            lineItemsCount: lineItems.length
                          });
                        }
                        
                        // Determine type display value
                        const typeDisplay = (entry.type && String(entry.type).trim()) || 
                                          (entry.reference_type && String(entry.reference_type).replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())) || 
                                          'Unknown';
                        const refNumber = (entry.reference_number && String(entry.reference_number).trim()) || 'N/A';
                        const projName = (entry.project_name && String(entry.project_name).trim()) || 'N/A';
                        const projNumber = (entry.project_number && String(entry.project_number).trim()) || '';
                        
                        // Debug computed values for entries with missing data or first few
                        if (hasMissingData || entryIndex < 3) {
                          console.log(`Computed values for entry ${entryIndex}:`, {
                            typeDisplay,
                            refNumber,
                            projName,
                            projNumber,
                            rawType: entry.type,
                            rawRefType: entry.reference_type,
                            rawRefNumber: entry.reference_number,
                            rawProjName: entry.project_name
                          });
                        }
                        
                        return (
                          <React.Fragment key={entryIndex}>
                            {/* Header row for the entry */}
                            <tr className="bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap" rowSpan={rowSpan}>
                                {typeDisplay && typeDisplay !== '' ? (
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    typeDisplay === 'AP Invoice' || entry.type === 'AP Invoice'
                                      ? 'bg-red-100 text-red-800' 
                                      : typeDisplay === 'Project Billing' || entry.type === 'Project Billing'
                                      ? 'bg-green-100 text-green-800'
                                      : typeDisplay === 'Labor Cost' || entry.type === 'Labor Cost'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-purple-100 text-purple-800'
                                  }`}>
                                    {typeDisplay}
                                  </span>
                                ) : (
                                  <span className="text-red-600 text-xs">MISSING TYPE</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium" rowSpan={rowSpan}>
                                {refNumber && refNumber !== '' ? refNumber : <span className="text-red-600 text-xs">MISSING REF</span>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" rowSpan={rowSpan}>
                                {projName && projName !== '' ? (
                                  <div>
                                    <div className="font-medium">{projName}</div>
                                    {projNumber && (
                                      <div className="text-gray-500 text-xs">{projNumber}</div>
                                    )}
                                    {entry.is_synced && (
                                      <div className="text-xs text-green-600 mt-1">✓ Synced</div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-red-600 text-xs">MISSING PROJECT</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                                {lineItems.length > 0 && lineItems[0] && (lineItems[0].debit_amount || 0) > 0 ? (
                                  <div>
                                    <div className="font-medium">{formatCurrency(lineItems[0].debit_amount || 0)}</div>
                                    <div className="text-xs text-gray-500">{lineItems[0].account_name || 'N/A'}</div>
                                  </div>
                                ) : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                                {lineItems.length > 0 && lineItems[0] && (lineItems[0].credit_amount || 0) > 0 ? (
                                  <div>
                                    <div className="font-medium">{formatCurrency(lineItems[0].credit_amount || 0)}</div>
                                    <div className="text-xs text-gray-500">{lineItems[0].account_name || 'N/A'}</div>
                                  </div>
                                ) : '-'}
                              </td>
                            </tr>
                            {/* Additional line items */}
                            {lineItems.slice(1).map((lineItem, lineIndex) => (
                              lineItem ? (
                                <tr key={`${entryIndex}-${lineIndex + 1}`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {(lineItem.debit_amount || 0) > 0 ? (
                                      <div>
                                        <div className="font-medium">{formatCurrency(lineItem.debit_amount || 0)}</div>
                                        <div className="text-xs text-gray-500">{lineItem.account_name || 'N/A'}</div>
                                      </div>
                                    ) : '-'}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {(lineItem.credit_amount || 0) > 0 ? (
                                      <div>
                                        <div className="font-medium">{formatCurrency(lineItem.credit_amount || 0)}</div>
                                        <div className="text-xs text-gray-500">{lineItem.account_name || 'N/A'}</div>
                                      </div>
                                    ) : '-'}
                                  </td>
                                </tr>
                              ) : null
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Modal Actions */}
                <div className="mt-6 flex justify-between items-center">
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCsvExport}
                      disabled={csvExportLoading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-md transition-colors flex items-center gap-2"
                    >
                      {csvExportLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Exporting...
                        </>
                      ) : (
                        <>
                          Export Detailed CSV
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleSummaryCsvExport}
                      disabled={csvExportLoading}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-md transition-colors flex items-center gap-2"
                    >
                      {csvExportLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 + 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Exporting...
                        </>
                      ) : (
                        <>
                          Export Summary CSV
                        </>
                      )}
                    </button>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowPreviewModal(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      Close Preview
                    </button>
                    <button
                      onClick={() => {
                        setShowPreviewModal(false);
                        handleCloseMonth();
                      }}
                      className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                      style={{ color: '#7F1D1D' }}
                    >
                      Close Month & Generate Entries
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Change Orders Modal */}
        {showPendingCOModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Pending Change Orders Included in Forecast</h3>
                  <button 
                    onClick={() => setShowPendingCOModal(false)} 
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {pcoLoading ? (
                  <p className="text-center text-gray-600">Loading pending change orders...</p>
                ) : pendingChangeOrders.length === 0 ? (
                  <p className="text-center text-gray-600">No pending change orders included in forecast for this period.</p>
                ) : (
                  <div className="mt-4 max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pendingChangeOrders.map((pco) => (
                          <tr key={pco.vuid}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{pco.reference_number}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{pco.description}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                              {formatCurrency(pco.revenue_amount || 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                              {formatCurrency(pco.cost_amount || 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                              <button
                                onClick={() => handleRemovePendingCO(pco.vuid)}
                                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                Remove from Forecast
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-black text-base font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm"
                    onClick={() => setShowPendingCOModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calculation Details Modal */}
        {showCalculationModal && calculationData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Calculation Details</h2>
                <button
                  onClick={() => {
                    setShowCalculationModal(false);
                    setCalculationData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {calculationData.project_number} - {calculationData.project_name}
                </h3>
              </div>

              {/* % Complete Calculation */}
              <div className="mb-6 bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h4 className="text-lg font-semibold text-blue-900 mb-4">% Complete Calculation</h4>
                {calculationData.uses_contract_item_fee_level ? (
                  <div className="space-y-3 text-sm">
                    <div className="bg-blue-100 rounded p-3 mb-3">
                      <p className="text-xs text-blue-800 font-medium">Contract Item Fee Level Method</p>
                    </div>
                    <div className="text-gray-700 text-sm mb-3">
                      <p className="mb-2">Project-level calculation (only cost codes with fees):</p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li>Percent Complete = (Costs for Cost Codes with Fees ÷ Budget for Cost Codes with Fees) × 100</li>
                        <li className="text-xs text-gray-600 mt-2">Note: Cost codes with no fee are excluded from percent complete calculation</li>
                      </ul>
                    </div>
                    {calculationData.detailed_breakdown?.percent_complete ? (
                      <div className="space-y-3">
                        <div className="border border-gray-300 rounded p-3 bg-white">
                          <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                            <div>
                              <span className="text-gray-600">Total Costs (with fees):</span>
                              <span className="font-semibold ml-2">{formatCurrency(calculationData.detailed_breakdown.percent_complete.total_costs_with_fees)}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Total Budget (with fees):</span>
                              <span className="font-semibold ml-2">{formatCurrency(calculationData.detailed_breakdown.percent_complete.total_budget_with_fees)}</span>
                            </div>
                          </div>
                          <div className="border-t pt-2 mb-3">
                            <div className="text-sm font-mono">
                              ({formatCurrency(calculationData.detailed_breakdown.percent_complete.total_costs_with_fees)} ÷ {formatCurrency(calculationData.detailed_breakdown.percent_complete.total_budget_with_fees)}) × 100 = {formatPercent(calculationData.detailed_breakdown.percent_complete.project_percent_complete)}
                            </div>
                          </div>
                          <div className="border-t pt-2">
                            <div className="text-xs text-gray-600 mb-2 font-semibold">Cost Codes Breakdown:</div>
                            <div className="max-h-48 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-100 sticky top-0">
                                  <tr>
                                    <th className="px-2 py-1 text-left">Cost Code</th>
                                    <th className="px-2 py-1 text-right">Costs</th>
                                    <th className="px-2 py-1 text-right">Budget</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {calculationData.detailed_breakdown.percent_complete.cost_codes_breakdown.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="px-2 py-1">
                                        <div className="font-medium">{item.cost_code}</div>
                                        <div className="text-gray-500 text-xs">{item.cost_code_description}</div>
                                      </td>
                                      <td className="px-2 py-1 text-right">{formatCurrency(item.costs)}</td>
                                      <td className="px-2 py-1 text-right">{formatCurrency(item.budget)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-blue-300 pt-3 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700 font-semibold">% Complete:</span>
                            <span className="font-bold text-blue-900 text-lg">{formatPercent(calculationData.detailed_breakdown.percent_complete.project_percent_complete)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t border-blue-300 pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-semibold">% Complete:</span>
                          <span className="font-bold text-blue-900 text-lg">{formatPercent(calculationData.percent_complete)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Costs to Date:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(calculationData.costs_to_date)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">{calculationData.budget_label}:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(calculationData.budget_value)}</span>
                    </div>
                    <div className="border-t border-blue-300 pt-3 mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700">Formula:</span>
                        <span className="text-xs text-gray-600 font-mono">(Costs to Date ÷ Budget) × 100</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Calculation:</span>
                        <span className="font-semibold text-gray-900 font-mono text-xs">
                          ({formatCurrency(calculationData.costs_to_date)} ÷ {formatCurrency(calculationData.budget_value)}) × 100
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-blue-300">
                        <span className="text-gray-700 font-semibold">% Complete:</span>
                        <span className="font-bold text-blue-900 text-lg">{formatPercent(calculationData.percent_complete)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Earned Revenue Calculation */}
              <div className="mb-6 bg-green-50 rounded-lg p-6 border border-green-200">
                <h4 className="text-lg font-semibold text-green-900 mb-4">Earned Revenue Calculation</h4>
                {calculationData.uses_contract_item_fee_level ? (
                  <div className="space-y-3 text-sm">
                    <div className="bg-green-100 rounded p-3 mb-3">
                      <p className="text-xs text-green-800 font-medium">Contract Item Fee Level Method</p>
                    </div>
                    <div className="text-gray-700 text-sm mb-3">
                      <p className="mb-2">For each cost code and cost type combination:</p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li>If fee exists: Total Fee = Budget × Fee %</li>
                        <li>Earned Fee = Total Fee × Project % Complete ({calculationData.detailed_breakdown?.revenue_recognized?.project_percent_complete ? formatPercent(calculationData.detailed_breakdown.revenue_recognized.project_percent_complete) : 'N/A'})</li>
                        <li>Revenue = Actual Cost + Earned Fee</li>
                        <li>Total Revenue = Sum of all revenue across combinations</li>
                      </ul>
                    </div>
                    {calculationData.detailed_breakdown?.revenue_recognized?.breakdown ? (
                      <div className="space-y-3">
                        <div className="border border-gray-300 rounded p-3 bg-white">
                          <div className="text-xs text-gray-600 mb-2 font-semibold">Revenue Breakdown by Cost Code/Cost Type:</div>
                          <div className="max-h-96 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1 text-left">Cost Code</th>
                                  <th className="px-2 py-1 text-left">Cost Type</th>
                                  <th className="px-2 py-1 text-right">Budget</th>
                                  <th className="px-2 py-1 text-right">Fee %</th>
                                  <th className="px-2 py-1 text-right">Total Fee</th>
                                  <th className="px-2 py-1 text-right">Actual Cost</th>
                                  <th className="px-2 py-1 text-right">Earned Fee</th>
                                  <th className="px-2 py-1 text-right font-semibold">Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {calculationData.detailed_breakdown.revenue_recognized.breakdown.map((item, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="px-2 py-1">
                                      <div className="font-medium">{item.cost_code}</div>
                                      <div className="text-gray-500 text-xs">{item.cost_code_description}</div>
                                    </td>
                                    <td className="px-2 py-1">{item.cost_type}</td>
                                    <td className="px-2 py-1 text-right">{formatCurrency(item.budget)}</td>
                                    <td className="px-2 py-1 text-right">{item.fee_percentage > 0 ? `${item.fee_percentage.toFixed(2)}%` : '-'}</td>
                                    <td className="px-2 py-1 text-right">{item.total_fee > 0 ? formatCurrency(item.total_fee) : '-'}</td>
                                    <td className="px-2 py-1 text-right">{formatCurrency(item.actual_cost)}</td>
                                    <td className="px-2 py-1 text-right">{item.earned_fee > 0 ? formatCurrency(item.earned_fee) : '-'}</td>
                                    <td className="px-2 py-1 text-right font-semibold">{formatCurrency(item.revenue)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t-2 border-green-600 font-bold bg-green-50">
                                  <td colSpan="7" className="px-2 py-2 text-right">Total Revenue:</td>
                                  <td className="px-2 py-2 text-right">{formatCurrency(calculationData.detailed_breakdown.revenue_recognized.total_revenue)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="border-t border-green-300 pt-3 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700 font-semibold">Earned Revenue:</span>
                            <span className="font-bold text-green-900 text-lg">{formatCurrency(calculationData.detailed_breakdown.revenue_recognized.total_revenue)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t border-green-300 pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-semibold">Earned Revenue:</span>
                          <span className="font-bold text-green-900 text-lg">{formatCurrency(calculationData.revenue_recognized)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">% Complete:</span>
                      <span className="font-semibold text-gray-900">{formatPercent(calculationData.percent_complete)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Total Contract Amount:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(calculationData.total_contract_amount)}</span>
                    </div>
                    <div className="border-t border-green-300 pt-3 mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700">Formula:</span>
                        <span className="text-xs text-gray-600 font-mono">(Costs to Date ÷ Budget) × Total Contract</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Calculation:</span>
                        <span className="font-semibold text-gray-900 font-mono text-xs">
                          ({formatCurrency(calculationData.costs_to_date)} ÷ {formatCurrency(calculationData.budget_value)}) × {formatCurrency(calculationData.total_contract_amount)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-green-300">
                        <span className="text-gray-700 font-semibold">Earned Revenue:</span>
                        <span className="font-bold text-green-900 text-lg">{formatCurrency(calculationData.revenue_recognized)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowCalculationModal(false);
                    setCalculationData(null);
                  }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WIP;
